/**
 * Converts a timestamp string into a normalized key used for grouping,
 * based on special fake-year encodings:
 * - 1900 → hourly ("HH:MM")
 * - 1901 → monthly ("Jan")
 * - 1902 → quarterly ("Q1")
 * - 1903 → weekday ("Mon")
 * - other → full date ("YYYY-MM-DD")
 */
const CACHE: Record<string, string> = {};

export function getDateAggregationKey(raw: string, mode: "day" | "datetime" = "datetime"): string {
  const cacheKey = `${raw}_${mode}`;
  if (CACHE[cacheKey]) return CACHE[cacheKey];

  const d = new Date(raw);
  const y = d.getFullYear();

  let result: string;
  if (y === 1900) result = d.toISOString().slice(11, 16); // "HH:MM"
  else if (y === 1901) result = d.toLocaleString("en-GB", { month: "short" }); // "Jan"
  else if (y === 1902) result = `Q${Math.floor(d.getMonth() / 3) + 1}`; // "Q1"
  else if (y === 1903) result = d.toLocaleString("en-GB", { weekday: "short" }); // "Mon"
  else if (y === 1904) result = d.getDate().toString();
  else {
    const pad = (n: number) => n.toString().padStart(2, "0");
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    result = mode === "day" ? date : `${date} ${time}`;
  }

  CACHE[cacheKey] = result;
  return result;
}


export function inferGlobalAggregationMode(results: { time: string }[][]): "day" | "datetime" {
  for (const result of results) {
    const countsPerDay: Record<string, number> = {};
    for (const { time } of result) {
      const day = new Date(time).toISOString().split("T")[0]; // YYYY-MM-DD
      countsPerDay[day] = (countsPerDay[day] || 0) + 1;
    }
    const max = Math.max(...Object.values(countsPerDay));
    if (max > 1) return "datetime"; // If any strategy has >1 point per day
  }
  return "day";
}

